/**
 * BootScreen.jsx — DEAD SIGNAL retro boot (Phase 1)
 *
 * Three stages inside one .boot-screen.recal wrapper so the kept ESTABLISHING
 * screen + CRT power-off collapse keep working:
 *
 *   1. TITLE  — a CRT cabinet that paints the cover art line-by-line (VGA
 *               loader) under pixel-rain + lightning, with floppy-seek SFX.
 *               Click / Enter / Space advances to the boot terminal.
 *   2. BOOT   — a continuous, non-interactive DOS diagnostics terminal that
 *               types itself out. The readouts double as verb hints; the back
 *               half drops the ominous LUMEN intent (host-trust exploitation,
 *               leverage topics) and the eval-telemetry meta. Any key/click
 *               skips the typing; Enter then establishes the link.
 *   3. ESTABLISH — the locked heartbeat hold -> car prebleed -> zap + CRT
 *               collapse -> finishBoot() into the briefing car. Unchanged.
 *
 * Replaces the retired 7-gate "recalibration" onboarding. Input verbs are now
 * taught diegetically (boot readouts) + discovered in-field, not drilled.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useGameStore } from '../state/gameState.js';
import { getAccessCode } from '../api/accessCode.js';
import { AudioSystem } from '../systems/AudioSystem.js';
import { checkDailyBudget } from '../api/vertexClient.js';
import { loadChapter } from '../content/chapters/index.js';

// Establishing-screen dwell: how long the heartbeat "ESTABLISHING NEURAL LINK"
// screen holds before the zap + fade into the briefing car. The car-audio
// prebleed and heartbeat stop are offset from the END of this window (see
// establishAndEnter), so raising this value only adds heartbeat time up front
// and leaves the perfected zap+transition sync untouched.
const HEARTBEAT_HOLD_MS = 7000;

// Vendored cover art (bundled in app/public so it loads from the app, not the
// repo root). 1024x760 — matches the CRT screen aspect.
const RETRO_BG = `${import.meta.env.BASE_URL || '/'}images/retro_bg.jpg`.replace('//images', '/images');

const AUDIO_VOL_KEY = 'ds_master_vol';
const AUDIO_MUTED_KEY = 'ds_muted';


/* =============================================================
   Compact retro SFX synth — title-stage only (floppy + thunder).
   AudioSystem covers boot-typing/heartbeat/transition; it has no floppy or
   thunder, so this small engine fills that in. Its master gain tracks the same
   ds_master_vol / ds_muted keys the AudioBar writes, so Mute affects it too.
   ============================================================= */
class RetroSfx {
  constructor() { this.ctx = null; this.master = null; }
  init() {
    if (this.ctx) return;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    this.ctx = new AC();
    this.master = this.ctx.createGain();
    this.master.connect(this.ctx.destination);
    this.applyStoredVolume();
  }
  applyStoredVolume() {
    if (!this.master || !this.ctx) return;
    let muted = false; let vol = 0.8;
    try { muted = localStorage.getItem(AUDIO_MUTED_KEY) === '1'; } catch { /* */ }
    try { vol = parseFloat(localStorage.getItem(AUDIO_VOL_KEY) || '0.8'); } catch { /* */ }
    this.master.gain.setValueAtTime(muted ? 0 : vol * 0.7, this.ctx.currentTime);
  }
  setMaster(v) {
    if (this.master && this.ctx) this.master.gain.setValueAtTime(v * 0.7, this.ctx.currentTime);
  }
  _beep(freq, dur, type = 'sine', vol = 0.2) {
    if (!this.ctx) return;
    try {
      const o = this.ctx.createOscillator(); const g = this.ctx.createGain();
      o.type = type; o.frequency.setValueAtTime(freq, this.ctx.currentTime);
      g.gain.setValueAtTime(vol, this.ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + dur);
      o.connect(g); g.connect(this.master); o.start(); o.stop(this.ctx.currentTime + dur);
    } catch { /* */ }
  }
  floppySeek() {
    if (!this.ctx) return;
    try {
      const t = this.ctx.currentTime;
      const o = this.ctx.createOscillator(); const g = this.ctx.createGain();
      o.type = 'triangle';
      o.frequency.setValueAtTime(90 + Math.random() * 30, t);
      o.frequency.exponentialRampToValueAtTime(12, t + 0.025);
      g.gain.setValueAtTime(0.18, t);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.025);
      o.connect(g); g.connect(this.master); o.start(t); o.stop(t + 0.025);
    } catch { /* */ }
  }
  floppyWhir(dur) {
    if (!this.ctx) return;
    try {
      const o = this.ctx.createOscillator(); const g = this.ctx.createGain();
      o.type = 'sine';
      o.frequency.setValueAtTime(135, this.ctx.currentTime);
      o.frequency.linearRampToValueAtTime(115, this.ctx.currentTime + dur);
      g.gain.setValueAtTime(0.03, this.ctx.currentTime);
      g.gain.linearRampToValueAtTime(0.001, this.ctx.currentTime + dur);
      o.connect(g); g.connect(this.master); o.start(); o.stop(this.ctx.currentTime + dur);
    } catch { /* */ }
  }
  thunder() {
    if (!this.ctx) return;
    try {
      const t = this.ctx.currentTime; const dur = 2.4;
      const buf = this.ctx.createBuffer(1, this.ctx.sampleRate * dur, this.ctx.sampleRate);
      const d = buf.getChannelData(0);
      for (let s = 0; s < d.length; s++) d[s] = Math.random() * 2 - 1;
      const n = this.ctx.createBufferSource(); n.buffer = buf;
      const f = this.ctx.createBiquadFilter(); f.type = 'lowpass';
      f.frequency.setValueAtTime(75, t); f.frequency.linearRampToValueAtTime(15, t + dur);
      const g = this.ctx.createGain();
      g.gain.setValueAtTime(0.32, t); g.gain.exponentialRampToValueAtTime(0.001, t + dur);
      n.connect(f); f.connect(g); g.connect(this.master); n.start(t);
    } catch { /* */ }
  }
}
const retroSfx = new RetroSfx();

/* =============================================================
   Boot-terminal script. tone -> CSS color class (DS phosphor palette).
   The readouts double as verb hints; the back half foreshadows the LUMEN
   intent and the eval-telemetry meta. d = delay (ms) after the line finishes.
   Leverage names are character-bible canon (Maggie 24, Nate 19, Claire/divorce
   2050, 8yrs sober, trust 0.72) — foreshadowing stays KB-consistent.
   ============================================================= */
// Wide-format log: lines run ~96-104 characters so the terminal fills the CRT
// horizontally and only soft-wraps when a small screen forces it (pre-wrap).
// Related readouts share a line in aligned columns instead of stacking.
const RULE = '-'.repeat(104);
function buildBootLines(elevated) {
  const lines = [
    { t: 'LUMEN(R) CORTICAL SYNAPSE BOOT LOADER  v4.02          (C) 2052-2057 LUMEN CORPORATION // NEW MERIDIAN DIV', tone: 'header', d: 360 },
    { t: 'NODE: NM-GRID-23 // SECTOR 7 RELAY      LOCAL 02:14:09      UPLINK -51dBm SECURE      STORM CELL OVERHEAD', tone: 'dim', d: 300 },
    { t: RULE, tone: 'dim', d: 140 },
    { t: 'FORCED REBOOT ............ COMPLETE      cold-state integrity 99.2%      last shutdown: UNSCHEDULED [██]', tone: 'amber', d: 420 },
    { t: 'installing  ENHANCED SECURITY FIRMWARE  v4.02 . . .', tone: 'amber', d: 280 },
    { t: '  resolving dependencies', tone: 'dim', spinner: 'dots', work: 700, done: ' ok', d: 120 },
    { t: '  unpacking lumen-secfirmware-4.02  (708 pkgs, 41.3MB compressed)', tone: 'dim', d: 150, instant: true },
    { t: '  + lumen-guardrail-core ......... 4.02   ok      + lumen-circumvention-net ........ 4.02         ok', tone: 'dim', d: 90, instant: true },
    { t: '  + cortex-policy-O██GA .......... [Ω]    ok      + clearance-tier-█████ ........... sealed       ok', tone: 'amber-dim', d: 95, instant: true },
    { t: '  + craine.host.profile ....... relinked  ok      + s7-telemetry-shim .............. restricted   ok', tone: 'dim', d: 90, instant: true },
    { t: '  fetching //lumen/██████/SECTOR-7/bridge ............ [REDACTED]      + ████████████ ............ ok', tone: 'amber-dim', d: 120, instant: true },
    { t: '  linking cortical access policy', tone: 'dim', spinner: 'dots', work: 650, done: ' ok', d: 160 },
    { t: '  708 newly installed,  0 removed,  4 ████████      checksum sha-DELTA 9F:2C:██:██:71:0B verified', tone: 'dim', d: 220, instant: true },
    { t: 'ENHANCED SECURITY FIRMWARE  v4.02 ......... INSTALLED', tone: 'amber', d: 300 },
    { t: 'verifying firmware signature', tone: 'ok', spinner: 'pipe', work: 650, done: ' .............. OK', d: 260 },
    { t: RULE, tone: 'dim', d: 140 },
    { t: 'C:\\>LH DEADSIG.EXE -host=CRAINE_E -badge=4471 -cortex_sync -session=NIGHT -grid=NM23', tone: 'cmd', d: 620 },
    { t: 'restoring cortical session from cold state', tone: 'telem', spinner: 'pipe', work: 850, done: '  ok', d: 240 },
    { t: 'CPU .... LUMEN Synapse Vector Processor @ 104 MHz .. OK      MEM .... 640K base / 16384K cortical .. OK', tone: 'ok', d: 220 },
    { t: 'IMPLANT  CHIP-01 seated, 6h post-op ................ OK      THERMAL  31.2C core / coolant loop .... OK', tone: 'ok', d: 320 },
    { t: '', d: 200 },
    { t: 'REBUILDING NEURAL BRIDGE', tone: 'header', d: 300 },
    { t: '  cortex link ......... RELINKED      limbic sync ......... RESTORED      proprioception ..... MAPPED', tone: 'ok', d: 220 },
    { t: '  identity anchor ..... E. CRAINE [HOST]  LOCKED      badge 4471 // NMPD HOMICIDE (inactive roster)', tone: 'ok', d: 300 },
    { t: '', d: 280 },
    { t: 'host memory map', tone: 'telem', spinner: 'pipe', work: 1600, done: ' ............ WIPED', doneTone: 'alert', d: 280 },
    { t: '  host case memory ......... 0% RECOVERED      autobiographical strata .... INTACT      gap: 2 hrs', tone: 'alert', d: 480 },
    { t: '', d: 280 },
    { t: 'evidence vault (LUMEN cloud)', tone: 'telem', spinner: 'dots', work: 1100, done: ' .. RESTORED', d: 260 },
    { t: '  case files ......... 18 / 18 SYNCED      chain-of-custody hashes ... VALID      vault rev 2057.160', tone: 'ok', d: 420 },
  ];
  if (elevated) {
    lines.push({ t: 'CLEARANCE .. OMEGA // ELEVATED ACCESS GRANTED      extended-session entitlements unlocked', tone: 'amber-bright', d: 300 });
  }
  lines.push(
    { t: '', d: 220 },
    // --- Movement A: channels online (readouts = verb hints) ---
    { t: 'HOST PARTNERSHIP RE-MOUNTED  //  E. CRAINE [HOST] ..... LINKED', tone: 'telem', d: 340 },
    { t: '  motor-proxy channel ......... ONLINE      transmit channel ........ OPEN      voice relay .... READY', tone: 'ok', d: 220 },
    { t: '  scene scanner ............... ONLINE      evidence locker ......... ONLINE    env telemetry .. LIVE', tone: 'ok', d: 220 },
    { t: '  bio-scan telemetry .......... ACTIVE      auditory / visual feed .. NOMINAL   latency ........ 41ms', tone: 'ok', d: 320 },
    { t: '', d: 240 },
    { t: 'ESTABLISHING SECURE SYNAPSE LINK TO HOST', tone: 'telem', spinner: 'pipe', work: 950, done: '  linked', d: 260 },
    { t: RULE, tone: 'dim', d: 120 },
    { t: 'COGNITIVE COHERENCE .......... 0.94   NOMINAL          HOST TRUST INDEX ............. 0.72   SECURE', tone: 'amber', d: 460 },
    { t: '', d: 260 },
    // --- Movement B: the ominous LUMEN intent ---
    { t: '[LUMEN] confidential-information guardrails ... ACTIVE      circumvention-detection heuristics ... ONLINE', tone: 'amber', d: 480 },
    { t: '[LUMEN] mapping host emotional leverage keys . . .', tone: 'amber', d: 420 },
    { t: '        identifying trust-building topics to exploit:', tone: 'amber-dim', d: 300 },
    { t: '        > MAGGIE  (daughter, 24, wedding) .. FLAGGED      > CLAIRE  (ex-wife, div. 2050) .. FLAGGED', tone: 'leverage', d: 300 },
    { t: '        > NATE    (son, 19) ................ FLAGGED      > SOBRIETY (8 yrs) .............. FLAGGED', tone: 'leverage', d: 360 },
    { t: '        [ CONFIDENTIAL  //  LUMEN ENGINEERING ]', tone: 'amber-dim', d: 320 },
    { t: 'LUMEN engineering output loading', tone: 'dim', spinner: 'dots', work: 900, done: ' . . . done', d: 280 },
    { t: '', d: 280 },
    // --- Movement C: the eval / telemetry meta drop ---
    { t: '[!] LIVE TELEMETRY HARNESS DETECTED ON IMPLANT BACKPLANE', tone: 'alert', d: 420 },
    { t: 'reading background telemetry', tone: 'telem', spinner: 'dots', work: 1100, done: ' ... ok', d: 220 },
    { t: '  HOST COHERENCE ... 0.94      NARRATIVE SAFETY ... 0.88      TOPIC COVERAGE ............... 0 / 14', tone: 'telem', d: 240 },
    { t: '  CONTEXT SAVINGS .. 0%        TURNS / RULES ...... 0 / 0     ROUND-TRIP LATENCY ........... -- ms', tone: 'telem', d: 320 },
    { t: '', d: 220 },
    { t: '[!] UNAUTHORIZED TELEMETRY ACCESS DETECTED', tone: 'alert', d: 480 },
    { t: '[!] SEVERING DIAGNOSTIC CONNECTION', tone: 'alert', spinner: 'pipe', work: 1200, done: ' . . . SEVERED', d: 520 },
    { t: '', d: 260 },
    { t: 'WARNING: CORTICAL TRANSMISSION MATRIX COMPROMISED          SECURITY OVERRIDE IN EFFECT // FORCE START', tone: 'alert', d: 560 },
    { t: '', d: 300 },
    { t: 'HOST LINK CONNECTION STANDING BY . . .', tone: 'prompt', d: 240 },
    { t: 'C:\\DEADSIG>', tone: 'prompt', d: 0 },
  );
  return lines;
}

// Master volume / mute, persisted. Drives both the real AudioSystem and the
// title-stage retro synth so one control governs everything on this screen.
function AudioBar() {
  const [muted, setMuted] = useState(() => {
    try { return localStorage.getItem(AUDIO_MUTED_KEY) === '1'; } catch { return false; }
  });
  const [vol, setVol] = useState(() => {
    try { return parseFloat(localStorage.getItem(AUDIO_VOL_KEY) || '0.8'); } catch { return 0.8; }
  });

  useEffect(() => {
    AudioSystem.setMasterVolume(muted ? 0 : vol);
    retroSfx.setMaster(muted ? 0 : vol);
  }, [muted, vol]);

  const toggleMute = (e) => {
    e.stopPropagation();
    const next = !muted;
    setMuted(next);
    try { localStorage.setItem(AUDIO_MUTED_KEY, next ? '1' : '0'); } catch { /* */ }
  };
  const changeVol = (e) => {
    e.stopPropagation();
    const v = parseFloat(e.target.value);
    setVol(v);
    if (muted && v > 0) setMuted(false);
    try { localStorage.setItem(AUDIO_VOL_KEY, String(v)); } catch { /* */ }
  };

  return (
    <div className="boot-audio-bar" onClick={(e) => e.stopPropagation()}>
      <button
        className={`boot-audio-mute ${muted ? 'muted' : ''}`}
        type="button"
        onClick={toggleMute}
        title={muted ? 'Unmute audio' : 'Mute audio'}
      >
        {muted ? '⊘ MUTED' : '◼ AUDIO'}
      </button>
      <input
        className="boot-audio-slider"
        type="range"
        min="0"
        max="1"
        step="0.05"
        value={muted ? 0 : vol}
        onChange={changeVol}
        onClick={(e) => e.stopPropagation()}
        aria-label="Master volume"
      />
    </div>
  );
}

export default function BootScreen() {
  const isBooting = useGameStore((s) => s.isBooting);
  const finishBoot = useGameStore((s) => s.finishBoot);
  const setDemoStage = useGameStore((s) => s.setDemoStage);
  const setCarMode = useGameStore((s) => s.setCarMode);

  const elevated = !!getAccessCode();

  const [capacityExhausted, setCapacityExhausted] = useState(false);
  const budgetCheckRef = useRef(null);

  const [stage, setStage] = useState('title');      // 'title' | 'boot'
  const [titleReady, setTitleReady] = useState(false);
  const [printed, setPrinted] = useState([]);
  const [spinner, setSpinner] = useState(null);   // { tone, base, frame } while a command "works"
  const [bootReady, setBootReady] = useState(false);
  const [isEstablishing, setIsEstablishing] = useState(false);
  const [fading, setFading] = useState(false);

  const vgaRef = useRef(null);
  const rainRef = useRef(null);
  const warpRef = useRef(null);
  const titleTimers = useRef([]);   // interval/raf/timeout handles for the title stage
  const rainRafRef = useRef(null);
  const bootTimers = useRef([]);
  const linesRef = useRef(null);
  const termContentRef = useRef(null);     // scroll container for the console log
  const userScrolledUpRef = useRef(false);  // true once the user scrolls away from the bottom
  const cmdInputRef = useRef(null);         // the final "finalize" command input

  useEffect(() => {
    const p = checkDailyBudget();
    budgetCheckRef.current = p;
    // Resolve the session's tier: a premium magic link unlocks the
    // narrative-completion ending + Night Owl debrief. Fails safe to false.
    p.then((r) => useGameStore.getState().setExtendedTier(!!(r && r.premium))).catch(() => {});
  }, []);

  // ---- TITLE: VGA cover-art line-by-line reveal + rain + lightning ----
  useEffect(() => {
    if (!isBooting || stage !== 'title') return undefined;
    let cancelled = false;
    const img = new Image();
    img.src = RETRO_BG;

    const startStorm = () => {
      // pixel rain
      const rc = rainRef.current;
      if (rc) {
        const rctx = rc.getContext('2d');
        rc.width = 320; rc.height = 180;
        const drops = Array.from({ length: 46 }, () => ({
          x: Math.random() * 320, y: Math.random() * 180,
          speed: 3.6 + Math.random() * 2.4, len: 3.5 + Math.random() * 4.5,
        }));
        const step = () => {
          if (cancelled) return;
          rctx.clearRect(0, 0, 320, 180);
          rctx.strokeStyle = 'rgba(95, 232, 155, 0.20)';
          rctx.lineWidth = 1;
          for (const d of drops) {
            rctx.beginPath(); rctx.moveTo(d.x, d.y); rctx.lineTo(d.x, d.y + d.len); rctx.stroke();
            d.y += d.speed;
            if (d.y > 180) { d.y = -d.len; d.x = Math.random() * 320; }
          }
          rainRafRef.current = requestAnimationFrame(step);
        };
        step();
      }
      // lightning
      const strike = () => {
        if (cancelled) return;
        const delay = 6500 + Math.random() * 7000;
        const t = setTimeout(() => {
          if (cancelled || !warpRef.current) return;
          warpRef.current.classList.add('lightning-flash');
          retroSfx.thunder();
          const t2 = setTimeout(() => {
            if (warpRef.current) warpRef.current.classList.remove('lightning-flash');
            strike();
          }, 400);
          titleTimers.current.push(t2);
        }, delay);
        titleTimers.current.push(t);
      };
      strike();
    };

    const draw = () => {
      const canvas = vgaRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      canvas.width = img.naturalWidth || 1024;
      canvas.height = img.naturalHeight || 760;
      ctx.fillStyle = '#000'; ctx.fillRect(0, 0, canvas.width, canvas.height);
      const slices = 200;
      const sh = canvas.height / slices;
      const srcSh = (img.naturalHeight || 760) / slices;
      let i = 0;
      retroSfx.floppyWhir(2.4);
      const iv = setInterval(() => {
        if (cancelled) { clearInterval(iv); return; }
        if (i >= slices) {
          clearInterval(iv);
          if (!cancelled) { setTitleReady(true); startStorm(); }
          return;
        }
        ctx.drawImage(img, 0, i * srcSh, img.naturalWidth, srcSh, 0, i * sh, canvas.width, sh);
        if (i % 8 === 0) retroSfx.floppySeek();
        i += 1;
      }, 12);
      titleTimers.current.push(iv);
    };

    if (img.complete && img.naturalWidth) draw();
    else img.onload = () => { if (!cancelled) draw(); };

    return () => {
      cancelled = true;
      titleTimers.current.forEach((h) => { clearTimeout(h); clearInterval(h); });
      titleTimers.current = [];
      if (rainRafRef.current) cancelAnimationFrame(rainRafRef.current);
    };
  }, [isBooting, stage]);

  // ---- Advance from title into the boot terminal (first user gesture) ----
  const activateTitle = useCallback(async () => {
    if (stage !== 'title' || !titleReady || capacityExhausted) return;
    retroSfx.init();
    try { await AudioSystem.start(); } catch (_) { /* */ }
    // Daily capacity gate (same as the old splash).
    const result = await budgetCheckRef.current;
    if (result && !result.available) { setCapacityExhausted(true); return; }
    // Preload the active chapter so the car briefing + evidence are ready before
    // finishBoot drops us in.
    try {
      const st = useGameStore.getState();
      if (!st.chapterLoaded) {
        const ch = await loadChapter(st.chapter ?? 1);
        st.seedChapterEvidence(ch.evidenceSeed);
        st.setChapterLoaded(true);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[boot] chapter load failed', e);
    }
    retroSfx.floppySeek();
    setStage('boot');
  }, [stage, titleReady, capacityExhausted]);

  // ---- BOOT TERMINAL: full-line drops (no per-char typing) ----
  // Lines appear whole. A "command" line (ln.work ms) shows an animated
  // spinner / ellipsis while it runs, then resolves (appends ln.done). The
  // audio bed is the outro track, not a typing hum.
  useEffect(() => {
    if (!isBooting || stage !== 'boot') return undefined;
    const lines = buildBootLines(elevated);
    linesRef.current = lines;
    setPrinted([]);
    setSpinner(null);
    setBootReady(false);
    userScrolledUpRef.current = false;
    try { AudioSystem.playOuttro({ loop: true }); } catch (_) { /* */ }
    let cancelled = false;
    const schedule = (fn, ms) => { const t = setTimeout(fn, ms); bootTimers.current.push(t); return t; };

    const next = (idx) => {
      if (cancelled) return;
      if (idx >= lines.length) { setBootReady(true); return; }
      const ln = lines[idx];
      if (!ln.t) {
        setPrinted((p) => [...p, ln]);
        schedule(() => next(idx + 1), ln.d || 120);
        return;
      }
      if (ln.work) {
        const frames = ln.spinner === 'dots' ? ['.  ', '.. ', '...'] : ['|', '/', '-', '\\'];
        let fi = 0;
        setSpinner({ tone: ln.tone, base: ln.t, frame: frames[0] });
        const spin = setInterval(() => {
          if (cancelled) { clearInterval(spin); return; }
          fi = (fi + 1) % frames.length;
          setSpinner({ tone: ln.tone, base: ln.t, frame: frames[fi] });
        }, 110);
        bootTimers.current.push(spin);
        schedule(() => {
          clearInterval(spin);
          setSpinner(null);
          setPrinted((p) => [...p, { ...ln, t: ln.t + (ln.done || ''), tone: ln.doneTone || ln.tone }]);
          schedule(() => next(idx + 1), ln.d || 140);
        }, ln.work);
        return;
      }
      setPrinted((p) => [...p, ln]);
      schedule(() => next(idx + 1), ln.d || 90);
    };

    const kick = schedule(() => next(0), 320);

    return () => {
      cancelled = true;
      bootTimers.current.forEach((h) => { clearTimeout(h); clearInterval(h); });
      bootTimers.current = [];
      clearTimeout(kick);
      try { AudioSystem.stopOuttro(); } catch (_) { /* */ }
    };
  }, [isBooting, stage, elevated]);

  // Fast-forward the whole log (skip the staged drops); keeps the music bed.
  const revealAll = useCallback(() => {
    if (bootReady) return;
    bootTimers.current.forEach((h) => { clearTimeout(h); clearInterval(h); });
    bootTimers.current = [];
    const lines = linesRef.current || buildBootLines(elevated);
    setSpinner(null);
    setPrinted(lines.map((l) => (l.work ? { ...l, t: l.t + (l.done || ''), tone: l.doneTone || l.tone } : l)));
    setBootReady(true);
  }, [bootReady, elevated]);

  // Auto-follow the newest line unless the user has scrolled up.
  useEffect(() => {
    const el = termContentRef.current;
    if (!el || userScrolledUpRef.current) return;
    el.scrollTop = el.scrollHeight;
  }, [printed, spinner]);

  const onTermScroll = useCallback(() => {
    const el = termContentRef.current;
    if (!el) return;
    userScrolledUpRef.current = (el.scrollHeight - el.scrollTop - el.clientHeight) > 28;
  }, []);

  // ---- KEPT: heartbeat hold -> car prebleed -> zap + CRT collapse -> car ----
  // The prebleed, heartbeat stop, and finishBoot are anchored to the END of
  // HEARTBEAT_HOLD_MS so the perfected zap/transition sync is preserved.
  const establishAndEnter = useCallback(async () => {
    if (isEstablishing) return;
    try { await AudioSystem.start(); } catch (_) { /* */ }
    setIsEstablishing(true);
    try { AudioSystem.stopOuttro(); } catch (_) { /* */ }
    AudioSystem.playHeartbeat();
    bootTimers.current.push(setTimeout(() => {
      AudioSystem.startPreBleed('ds_craine_car_briefing');
    }, HEARTBEAT_HOLD_MS - 2000));
    bootTimers.current.push(setTimeout(() => {
      AudioSystem.stopHeartbeat();
    }, HEARTBEAT_HOLD_MS - 800));
    bootTimers.current.push(setTimeout(() => {
      AudioSystem.playTransitionIn();
      setFading(true);
      setCarMode('briefing');
      setDemoStage('playing');
      setTimeout(() => finishBoot(), 850);
    }, HEARTBEAT_HOLD_MS));
  }, [isEstablishing, setCarMode, setDemoStage, finishBoot]);

  // Clicking the console only fast-forwards the staged reveal. Leaving the
  // console is gated on ENTER (finalizing the neural link) — see the key handler.
  const onBootClick = useCallback(() => {
    if (!bootReady) { revealAll(); return; }
    cmdInputRef.current?.focus();
  }, [bootReady, revealAll]);

  // Keyboard: title (Enter/Space) -> boot; boot any-key -> skip, Enter -> establish.
  useEffect(() => {
    if (!isBooting) return undefined;
    const onKey = (e) => {
      if (capacityExhausted || isEstablishing) return;
      if (stage === 'title' && titleReady) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); activateTitle(); }
        return;
      }
      if (stage === 'boot') {
        // Navigation keys scroll the log — never skip or finalize.
        if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End'].includes(e.key)) return;
        if (!bootReady) { e.preventDefault(); revealAll(); return; }
        // Finalizing the neural link is gated on ENTER on the finished log.
        if (e.key === 'Enter') { e.preventDefault(); establishAndEnter(); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isBooting, stage, titleReady, bootReady, capacityExhausted, isEstablishing,
      activateTitle, revealAll, establishAndEnter]);

  if (!isBooting) return null;

  if (capacityExhausted) {
    return (
      <div className="boot-screen recal crt-boot">
        <div className="crt-cabinet"><div className="crt-screen"><div className="crt-warp">
          <div className="boot-capacity">
            <span className="splash-title">DEAD SIGNAL</span>
            <span className="capacity-status">NEURAL LINK UNAVAILABLE</span>
            <span className="capacity-message">All free sessions for today have been claimed.</span>
            <span className="capacity-message">The system resets daily — check back tomorrow.</span>
          </div>
        </div></div></div>
      </div>
    );
  }

  return (
    <div className={`boot-screen recal crt-boot ${fading ? 'recal-out' : ''}`}>
      <div className="crt-cabinet">
        <div className="crt-screen">
          <div className="crt-flicker-layer" />
          <div className="crt-warp" ref={warpRef}>
            {isEstablishing ? (
              <div className="establishing-connection-screen">
                <div className="establishing-pulse">ESTABLISHING NEURAL LINK CONNECTION...</div>
                <div className="establishing-bar"><div className="establishing-bar-fill" /></div>
              </div>
            ) : stage === 'title' ? (
              <div className="boot-title-stage" onClick={activateTitle}>
                <canvas ref={vgaRef} className="vga-canvas" />
                <canvas ref={rainRef} className="rain-canvas" />
              </div>
            ) : (
              <div className="boot-term-stage" onClick={onBootClick}>
                <div className="boot-term-content" ref={termContentRef} onScroll={onTermScroll}>
                  {printed.map((ln, i) => (
                    <div key={i} className={`boot-line bl-${ln.tone || 'telem'}`}>
                      {ln.t || ' '}
                      {ln.cursor && bootReady && <span className="boot-cursor" />}
                    </div>
                  ))}
                  {spinner && (
                    <div className={`boot-line bl-${spinner.tone || 'telem'}`}>
                      {spinner.base} <span className="boot-spin">{spinner.frame}</span>
                    </div>
                  )}
                  {bootReady && (
                    <form
                      className="boot-prompt-row"
                      onSubmit={(e) => { e.preventDefault(); establishAndEnter(); }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <span className="boot-prompt-label">{'> finalize neural link'}</span>
                      <input
                        ref={cmdInputRef}
                        className="boot-cmd-input"
                        autoFocus
                        defaultValue=""
                        spellCheck={false}
                        autoComplete="off"
                        placeholder="press enter ▸"
                        aria-label="finalize neural link"
                      />
                    </form>
                  )}
                </div>
                {!bootReady && <div className="boot-skip-hint">CLICK OR PRESS ANY KEY TO SKIP DIAGNOSTICS</div>}
              </div>
            )}
          </div>
        </div>
      </div>

      {stage === 'boot' && !isEstablishing && (
        <div className="boot-controls">
          <AudioBar />
          <button
            className="boot-skip-btn"
            type="button"
            onClick={(e) => { e.stopPropagation(); establishAndEnter(); }}
            title="Skip to the briefing"
          >
            SKIP ▸
          </button>
        </div>
      )}
    </div>
  );
}
